import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../../services/api';
import { 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  FunnelIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const BlogPostsPage = () => {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [sortField, setSortField] = useState('published_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch blog posts data
  const { data: postsData, isLoading, isError, refetch } = useQuery(
    ['adminBlogPosts', sortField, sortDirection, filters, currentPage, itemsPerPage],
    async () => {
      // Use the centralized API service with the correct path
      const response = await api.get('/api/v1/admin/blog-posts', {
        params: {
          sort_by: sortField,
          sort_direction: sortDirection,
          page: currentPage,
          per_page: itemsPerPage,
          category: filters.category || undefined,
          status: filters.status || undefined,
          search: filters.search || undefined
        }
      });
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('Error fetching blog posts:', error);
      }
    }
  );

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    refetch();
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      category: '',
      status: '',
      search: ''
    });
    setCurrentPage(1);
    refetch();
  };

  // View blog post details
  const viewPostDetails = (post) => {
    setSelectedPost(post);
    setIsDetailOpen(true);
  };

  // Edit blog post
  const editPost = (post) => {
    setSelectedPost(post);
    setIsFormOpen(true);
  };

  // Add new blog post
  const addNewPost = () => {
    setSelectedPost(null);
    setIsFormOpen(true);
  };

  // Delete blog post
  const openDeleteModal = (post) => {
    setSelectedPost(post);
    setIsDeleteModalOpen(true);
  };

  // Handle blog post form submission
  const handlePostSubmit = async (postData) => {
    try {
      // Use the centralized API service with the correct path
      const url = selectedPost 
        ? `/api/v1/admin/blog-posts/${selectedPost.id}` 
        : `/api/v1/admin/blog-posts`;
      
      const method = selectedPost ? 'put' : 'post';
      
      // For multipart/form-data, we need to set the content type
      await api({
        method,
        url,
        data: postData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      refetch();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save blog post. Please try again.');
    }
  };

  // Handle blog post deletion
  const handleDeletePost = async () => {
    try {
      // Use the centralized API service with the correct path
      await api.delete(`/api/v1/admin/blog-posts/${selectedPost.id}`);
      
      refetch();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post. Please try again.');
    }
  };

  // Function to render sort indicator
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    
    return sortDirection === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 ml-1" /> 
      : <ArrowDownIcon className="h-4 w-4 ml-1" />;
  };

  // Function to render sort button
  const renderSortButton = (field, label) => (
    <button
      onClick={() => handleSort(field)}
      className="group inline-flex items-center text-left"
    >
      {label}
      <span className="ml-2 flex-none rounded text-gray-400 group-hover:visible group-focus:visible">
        {renderSortIcon(field)}
      </span>
    </button>
  );

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'published':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'draft':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'scheduled':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'archived':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Placeholder data for when API data is not available
  const placeholderPosts = {
    data: [
      {
        id: 1,
        title: 'Top 10 Safari Destinations in Kenya',
        slug: 'top-10-safari-destinations-kenya',
        excerpt: 'Discover the most breathtaking safari destinations Kenya has to offer, from Maasai Mara to Amboseli National Park.',
        content: '<p>Kenya is home to some of the most spectacular wildlife reserves in the world...</p>',
        featured_image: 'https://via.placeholder.com/800x600',
        category: 'Safari',
        author: {
          id: 1,
          name: 'John Kamau'
        },
        status: 'Published',
        published_at: '2023-03-15T10:30:00Z',
        created_at: '2023-03-10T08:15:00Z',
        updated_at: '2023-03-15T10:30:00Z',
        comments_count: 12,
        views_count: 1250
      },
      {
        id: 2,
        title: 'A Guide to Kenyan Cuisine',
        slug: 'guide-kenyan-cuisine',
        excerpt: 'Explore the rich flavors and traditional dishes that make Kenyan cuisine unique and delicious.',
        content: '<p>Kenyan cuisine is as diverse as its culture, with each region offering unique flavors and cooking styles...</p>',
        featured_image: 'https://via.placeholder.com/800x600',
        category: 'Culture',
        author: {
          id: 2,
          name: 'Sarah Wanjiku'
        },
        status: 'Published',
        published_at: '2023-03-20T14:45:00Z',
        created_at: '2023-03-18T11:30:00Z',
        updated_at: '2023-03-20T14:45:00Z',
        comments_count: 8,
        views_count: 980
      },
      {
        id: 3,
        title: 'Best Time to Visit Mount Kenya',
        slug: 'best-time-visit-mount-kenya',
        excerpt: 'Learn about the optimal seasons for climbing Mount Kenya and what to expect during different times of the year.',
        content: '<p>Mount Kenya, Africa\'s second-highest peak, offers different experiences depending on when you visit...</p>',
        featured_image: 'https://via.placeholder.com/800x600',
        category: 'Adventure',
        author: {
          id: 1,
          name: 'John Kamau'
        },
        status: 'Draft',
        published_at: null,
        created_at: '2023-03-25T09:20:00Z',
        updated_at: '2023-03-25T09:20:00Z',
        comments_count: 0,
        views_count: 0
      },
      {
        id: 4,
        title: 'Wildlife Photography Tips for Kenyan Safaris',
        slug: 'wildlife-photography-tips-kenyan-safaris',
        excerpt: 'Master the art of wildlife photography with these expert tips specifically for Kenyan safari adventures.',
        content: '<p>Capturing the perfect wildlife shot requires patience, skill, and knowledge of your environment...</p>',
        featured_image: 'https://via.placeholder.com/800x600',
        category: 'Photography',
        author: {
          id: 3,
          name: 'David Ochieng'
        },
        status: 'Scheduled',
        published_at: '2023-04-05T08:00:00Z',
        created_at: '2023-03-28T16:10:00Z',
        updated_at: '2023-03-28T16:10:00Z',
        comments_count: 0,
        views_count: 0
      },
      {
        id: 5,
        title: 'Coastal Gems: Exploring Kenya\'s Beaches',
        slug: 'coastal-gems-exploring-kenyas-beaches',
        excerpt: 'Discover the pristine beaches along Kenya\'s coastline, from Diani to Lamu Island.',
        content: '<p>Kenya\'s 536 km coastline boasts some of the most beautiful beaches in Africa...</p>',
        featured_image: 'https://via.placeholder.com/800x600',
        category: 'Beaches',
        author: {
          id: 2,
          name: 'Sarah Wanjiku'
        },
        status: 'Published',
        published_at: '2023-03-12T11:15:00Z',
        created_at: '2023-03-10T09:30:00Z',
        updated_at: '2023-03-12T11:15:00Z',
        comments_count: 15,
        views_count: 1420
      }
    ],
    meta: {
      total: 5,
      per_page: 10,
      current_page: 1,
      last_page: 1
    }
  };

  // Use placeholder data if API data is not available
  // The API returns {success: true, data: {...}, message: 'Blog posts retrieved successfully'}
  // where data contains the posts and pagination metadata
  const posts = postsData?.data?.data || postsData?.data || placeholderPosts.data;
  const meta = postsData?.data?.meta || postsData?.meta || placeholderPosts.meta;
  
  // Debug the data structure
  console.log('API Response Structure:', postsData);
  console.log('Posts data type:', typeof posts, Array.isArray(posts));
  
  // Ensure posts is always an array
  const postsArray = Array.isArray(posts) ? posts : [];
  console.log('Posts array length:', postsArray.length);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Blog Posts Management</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <FunnelIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={addNewPost}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Post
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white shadow rounded-lg p-4">
          <form onSubmit={applyFilters} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Categories</option>
                  <option value="safari">Safari</option>
                  <option value="adventure">Adventure</option>
                  <option value="culture">Culture</option>
                  <option value="photography">Photography</option>
                  <option value="beaches">Beaches</option>
                </select>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Statuses</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Title, content..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Reset
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Blog Posts Table */}
      <div className="bg-white shadow rounded-lg">
        {isLoading ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">Loading blog posts...</p>
          </div>
        ) : isError ? (
          <div className="p-6 text-center">
            <p className="text-red-500">Error loading blog posts. Please try again.</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Retry
            </button>
          </div>
        ) : postsArray.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-gray-500">No blog posts found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('title', 'Title')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('category', 'Category')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('author.name', 'Author')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('status', 'Status')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {renderSortButton('published_at', 'Published')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {postsArray.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img className="h-10 w-10 rounded object-cover" src={post.featured_image} alt={post.title} />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{post.title}</div>
                          <div className="text-xs text-gray-500">{truncateText(post.excerpt, 60)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{post.category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{post.author.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderStatusBadge(post.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(post.published_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => viewPostDetails(post)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => editPost(post)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit Post"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(post)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Post"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {meta && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(meta.last_page, currentPage + 1))}
                disabled={currentPage === meta.last_page}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, meta.total)}
                  </span>{' '}
                  of <span className="font-medium">{meta.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Previous</span>
                    <ArrowUpIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  {/* Page numbers would go here */}
                  <button
                    onClick={() => setCurrentPage(Math.min(meta.last_page, currentPage + 1))}
                    disabled={currentPage === meta.last_page}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <span className="sr-only">Next</span>
                    <ArrowDownIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedPost && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Blog Post</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete the blog post "{selectedPost.title}"? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDeletePost}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogPostsPage;
